<template>
  <div class="accounts">
    <Actions :items="actionsHeader" :right="true" />
    <MobileTable
      v-model:selection="store.state.account.selectedAccounts"
      :label="listLabel"
      :columns="columns"
      :data="tableRows"
    />
    <DesktopTable
      v-model:selection="store.state.account.selectedAccounts"
      :label="listLabel"
      :columns="columns"
      :data="tableRows"
    />
    <p v-if="showEmptyState" class="empty-note">No hay cuentas contables para mostrar.</p>
    <Actions :items="actionsFooter" />
  </div>
</template>

<script>
import { computed, reactive, onMounted, watch } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import Actions from "../../components/shared/Actions.vue";
import MobileTable from "../../widgets/tables/MobileTable.vue";
import DesktopTable from "../../widgets/tables/DesktopTable.vue";
import { openMode, actions } from "../../constants";
import { sortAccountsByCode } from "../../utils/accounting/accounts.mjs";

const ACCOUNT_TYPE_LABELS = {
  income: "Ingreso",
  expense: "Egreso",
};

export default {
  components: { Actions, MobileTable, DesktopTable },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const listLabel = computed(() => `${route.name || "Cuentas"} (${tableRows.value.length})`);

    const tableRows = computed(() =>
      sortAccountsByCode(store.state.account.accounts || []).map((item) => ({
        id: item.id,
        code: item.code || "",
        name: item.name || "",
        type: ACCOUNT_TYPE_LABELS[item.type] || item.type || "",
        currency: item.currency?.code || item.currency?.name || "",
        initialBalance: Number(item.initialBalance ?? 0),
        status: item.status === "inactive" ? "Inactiva" : "Activa",
      }))
    );

    const columns = reactive([
      { field: "code", header: "Código", sort: true },
      { field: "name", header: "Nombre", sort: true },
      { field: "type", header: "Tipo", sort: true },
      { field: "currency", header: "Moneda", sort: true },
      { field: "initialBalance", header: "Saldo inicial", currency: true, sort: true },
      { field: "status", header: "Estado", sort: true },
    ]);

    const showEmptyState = computed(
      () => !store.state.loading && Boolean(buildingId.value) && !tableRows.value.length
    );

    const notify = (type, message) => {
      store.commit("setToast", { type, message });
    };

    const ensureBreadcrumb = () => {
      const breadcrumbs = store.state.general.breadcrumbs;
      const hasCurrent = breadcrumbs.some((item) => item.to === route.path);
      if (!breadcrumbs.length) {
        store.commit("setBreadcrumbs", [
          { label: "Inicio", to: "/home" },
          { label: "Contabilidad", to: "/contabilidad" },
          { label: route.name, to: route.path },
        ]);
        return;
      }
      const hasContabilidad = breadcrumbs.some((item) => item.to === "/contabilidad");
      if (!hasContabilidad) {
        store.commit("setBreadcrumbs", [
          { label: "Inicio", to: "/home" },
          { label: "Contabilidad", to: "/contabilidad" },
          { label: route.name, to: route.path },
        ]);
        return;
      }
      if (!hasCurrent) {
        store.commit("addBreadcrumb", { label: route.name, to: route.path });
      }
    };

    const loadAccounts = async () => {
      if (!buildingId.value) return;
      const response = await store.dispatch(actions.accountActions.findAll, {
        buildingId: buildingId.value,
      });
      if (!response?.ok && response?.message) {
        notify("error", response.message);
      }
    };

    const requireSelection = () => {
      if (store.state.account.selectedAccounts.length !== 1) {
        notify("warn", "Debe seleccionar una cuenta");
        return false;
      }
      return true;
    };

    const onCreate = () => {
      store.commit("setOpenMode", openMode.CREATE);
      router.push("/contabilidad/cuenta/nuevo");
    };

    const onModify = () => {
      if (!requireSelection()) return;
      store.commit("setOpenMode", openMode.MODIFY);
      router.push("/contabilidad/cuenta/modificar");
    };

    const onDownloadExcel = async () => {
      if (!tableRows.value.length) {
        notify("warn", "No hay datos para exportar");
        return;
      }
      store.commit("setLoading", true);
      try {
        const request = {
          fileName: "cuentas-contables",
          headers: columns.map((column) => column.header),
          data: tableRows.value.map((row) => [
            row.code,
            row.name,
            row.type,
            row.currency,
            row.initialBalance,
            row.status,
          ]),
        };
        await store.dispatch(actions.accountActions.downloadExcel, request);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const actionsHeader = reactive([
      {
        name: "Descargar Excel",
        action: onDownloadExcel,
        hidden: false,
      },
    ]);

    const actionsFooter = reactive([
      {
        name: "Nueva",
        action: onCreate,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/cuenta/nuevo"),
      },
      {
        name: "Modificar",
        action: onModify,
        hidden: router.getRoutes().find((r) => r.path === "/contabilidad/cuenta/modificar"),
      },
    ]);

    onMounted(async () => {
      store.commit("setLoading", true);
      store.commit("setSelectedAccounts", []);
      ensureBreadcrumb();
      try {
        await loadAccounts();
      } finally {
        store.commit("setLoading", false);
      }
    });

    watch(buildingId, async (value, oldValue) => {
      if (value && value !== oldValue) {
        store.commit("setLoading", true);
        store.commit("setSelectedAccounts", []);
        try {
          await loadAccounts();
        } finally {
          store.commit("setLoading", false);
        }
      }
    });

    return {
      store,
      columns,
      tableRows,
      actionsHeader,
      actionsFooter,
      listLabel,
      showEmptyState,
    };
  },
};
</script>

<style scoped>
.accounts {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-bottom: 2rem;
}

.empty-note {
  margin: 0 1rem;
  text-align: center;
  color: #64748b;
}
</style>

